import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import CircularProgress from '@mui/material/CircularProgress';
import PendingFilePreview from './PendingFilePreview';

const ChatInput = ({
  userMessage,
  selectedUser,
  typingUserId,
  fileError,
  pendingFile,
  isUploadingFile,
  uploadProgress,
  showEmojiPicker,
  canSend,
  inputRef,
  fileInputRef,
  emojiPickerRef,
  onMessageChange,
  onInputBlur,
  onKeyDown,
  onSendMessage,
  onInsertEmoji,
  onToggleEmojiPicker,
  onFileButtonClick,
  onFileSelect,
  onRemovePendingFile,
  onClearFileError,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {typingUserId &&
        selectedUser &&
        typingUserId === selectedUser._id && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'primary.main',
              fontStyle: 'italic',
              mb: 0.5,
            }}
          >
            {selectedUser?.fname || selectedUser?.lname
              ? `${selectedUser.fname} ${selectedUser.lname}`.trim()
              : 'Someone'}{' '}
            is typing...
          </Typography>
        )}

      {fileError && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1,
            p: 1,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'error.main',
              fontSize: '0.75rem',
              flex: 1,
            }}
          >
            {fileError}
          </Typography>
          <IconButton
            size="small"
            onClick={onClearFileError}
            sx={{ color: 'error.main', p: 0.25 }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      )}

      {pendingFile && (
        <PendingFilePreview
          file={pendingFile}
          uploadProgress={isUploadingFile ? uploadProgress : null}
          onRemove={onRemovePendingFile}
        />
      )}

      {showEmojiPicker && (
        <Box
          ref={emojiPickerRef}
          sx={{
            position: 'absolute',
            bottom: '70px',
            left: '16px',
            zIndex: 9999,
            boxShadow: theme.shadows[8],
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Picker
            data={data}
            onEmojiSelect={(e) => onInsertEmoji(e.native)}
            theme={isDark ? 'dark' : 'light'}
            previewPosition="none"
            skinTonePosition="none"
          />
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,application/zip"
        onChange={onFileSelect}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: isDark
            ? alpha(theme.palette.common.white, 0.06)
            : alpha(theme.palette.grey[500], 0.08),
          borderRadius: 3,
          px: 1.5,
          py: 0.5,
          border: '1px solid',
          borderColor: isDark
            ? alpha(theme.palette.common.white, 0.1)
            : 'divider',
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
          transition: 'all 0.2s ease',
        }}
      >
        <Tooltip title="Add Emoji">
          <IconButton
            size="small"
            onClick={onToggleEmojiPicker}
            disabled={isUploadingFile}
            sx={{
              color: showEmojiPicker ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main' },
              flexShrink: 0,
            }}
          >
            <EmojiEmotionsOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Attach File (max 10 MB)">
          <IconButton
            size="small"
            onClick={onFileButtonClick}
            disabled={isUploadingFile || !!pendingFile}
            sx={{
              color: pendingFile ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main' },
              flexShrink: 0,
              '&.Mui-disabled': { color: 'action.disabled' },
            }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <TextField
          inputRef={inputRef}
          value={userMessage}
          onChange={onMessageChange}
          onBlur={onInputBlur}
          onKeyDown={onKeyDown}
          placeholder={
            isUploadingFile
              ? 'Uploading file...'
              : `Message ${selectedUser?.fname || 'your pair'}...`
          }
          multiline
          maxRows={4}
          variant="standard"
          fullWidth
          disabled={isUploadingFile}
          InputProps={{ disableUnderline: true }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.9rem',
              color: 'text.primary',
              py: 0.75,
              '&::placeholder': {
                color: 'text.secondary',
                opacity: 0.7,
              },
            },
          }}
        />

        <Tooltip
          title={
            isUploadingFile ? 'Uploading...' : 'Send Message (Enter)'
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={onSendMessage}
              disabled={!canSend}
              sx={{
                bgcolor: canSend ? 'primary.main' : 'transparent',
                color: canSend ? 'white' : 'text.secondary',
                width: 34,
                height: 34,
                flexShrink: 0,
                '&:hover': { bgcolor: 'primary.dark', color: 'white' },
                '&.Mui-disabled': {
                  color: 'text.secondary',
                  bgcolor: 'transparent',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isUploadingFile ? (
                <CircularProgress
                  size={16}
                  sx={{ color: 'text.secondary' }}
                />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontSize: '0.7rem',
          px: 1,
          mt: 0.5,
          display: 'block',
        }}
      >
        Press Enter to send · Shift+Enter for new line · 📎 max 10 MB
      </Typography>
    </Box>
  );
};

export default ChatInput;
